import { Operador } from 'services/axios/OperadoresService';
import { Cliente } from './ClientesService';
import axiosInstance from './axios';
import { apiBaseURL } from 'config/axios';
import Utils from 'services/Utils';
import { Paginated } from './Models';
import esLocale from "date-fns/locale/es";
import { format } from 'date-fns';
import { RRule, Frequency } from 'rrule';

const entityApiRoute = 'servicios';

export interface Servicio {
  fechaInicio: Date;
  horarioInicio: Date;
  fechaFinRecurrencia: Date;
  recurrenciaCheckbox: boolean;
  recurrencia: number;
  duracion: number;
  repetirCada: number;
  frecuenciaMensual: string;
  frecuencia: string;
  terminaOption: string;
  cliente?: Cliente;
  operadores: Operador[];
  servicios: TipoServicio[];
  precio?: string;
  comprobante: boolean;
  refuerzo: boolean;
};

export interface TipoServicio {
  id: number;
  key: string;
  description: string;
  precio: number;
}

export interface EstadoServicio {
  id: number;
  key: string;
  description: string;
  color: string;
}

export const duracionServicio = [
  { value: '15', label: '15 minutos' },
  { value: '30', label: '30 minutos' },
  { value: '45', label: '45 minutos' },
  { value: '60', label: '60 minutos' },
];

export enum Recurrencia {
  NO_REPITE = 1,
  CADA_MES_NRO_DIA = 2,
  CADA_MES_NTH_DIA = 3,
  CADA_ANO = 4,
  CADA_180_DIAS = 5,
  PERSONALIZAR = 6
}

export const frecuencias = (repetir: number) => [
  { value: Frequency.YEARLY.toString(), label: repetir > 1 ? 'Años' : 'Año' },
  { value: Frequency.MONTHLY.toString(), label: repetir > 1 ? 'Meses' : 'Mes' },
];

export const frecuenciaMensualOptions = (fechaInicio: Date) => {
  const { valueNroDia, textNroDia } = getNroDiaFromDate(fechaInicio);
  const { valueNthDia, textNthDia } = getNthDiaFromDate(fechaInicio);
  return [
    { value: valueNroDia, label: "Cada mes el día " + textNroDia },
    { value: valueNthDia, label: "Cada mes el " + textNthDia },
  ]
};

export const getNroDiaFromDate = (fecha: Date) => {
  const valueNroDia = " "; // cuando rrule repite por dia/mes solo pone monthly en la regla
  const textNroDia = format(fecha, "dd");
  return { valueNroDia, textNroDia }
}

export const mapNthToWords = (nth: number) => {
  switch (nth) {
    case 1: return "primer";
    case 2: return "segundo";
    case 3: return "tercer";
    case 4: return "cuarto";
    case -1: return "último";
  }
}

export const getNthDiaFromDate = (fecha: Date) => {
  const textNroDia = format(fecha, "dd");

  let textNombreDia = format(fecha, "EEEE", { locale: esLocale });
  textNombreDia = textNombreDia[0].toUpperCase() + textNombreDia.slice(1);

  //calcula que nth de dia es en el mes, ej: primer lunes, cuarto viernes.. -1 es el ultimo 
  let day: number = parseInt(textNroDia, 10);
  let nth = Math.floor(day / 7) + (day % 7 > 0 ? 1 : 0);
  nth = nth < 5 ? nth : -1;

  const valueNthDia = nth + format(fecha, "EEEEEE").toUpperCase();
  const textNthDia = `${mapNthToWords(nth)} ${textNombreDia}`;
  return { valueNthDia, textNthDia }
}

export const mapToByWeekDay = (nthString: string) => {
  const nth = parseInt(nthString);
  const day = nthString.substring(nthString.length - 2);
  switch (day) {
    case "MO": return [RRule.MO.nth(nth)];
    case "TU": return [RRule.TU.nth(nth)];
    case "WE": return [RRule.WE.nth(nth)];
    case "TH": return [RRule.TH.nth(nth)];
    case "FR": return [RRule.FR.nth(nth)];
    case "SA": return [RRule.SA.nth(nth)];
    case "SU": return [RRule.SU.nth(nth)];
  }
  return undefined;
}

export const recurrencias = (fecha: Date) => {
  let textMesAnual = format(fecha, "MMMM", { locale: esLocale });
  textMesAnual = textMesAnual[0].toUpperCase() + textMesAnual.slice(1);
  const textAnual = format(fecha, "dd") + " de " + textMesAnual;
  const { textNroDia } = getNroDiaFromDate(fecha);
  const { textNthDia } = getNthDiaFromDate(fecha);
  return [
    { value: Recurrencia.NO_REPITE, label: "No se repite" },
    { value: Recurrencia.CADA_MES_NRO_DIA, label: "Cada mes el día " + textNroDia },
    { value: Recurrencia.CADA_MES_NTH_DIA, label: "Cada mes el " + textNthDia },
    { value: Recurrencia.CADA_ANO, label: "Anualmente el " + textAnual },
    { value: Recurrencia.CADA_180_DIAS, label: "Cada 180 días corridos" },
    { value: Recurrencia.PERSONALIZAR, label: 'Personalizar' },
  ];
}

const createEntity = async (entity: Servicio) => {
  const url = `${apiBaseURL}/${entityApiRoute}`;
  await axiosInstance.post(url, entity);
}

const updateEntity = async (id: string, entity: Servicio) => {
  const url = `${apiBaseURL}/${entityApiRoute}/${id}`;
  await axiosInstance.patch(url, entity);
}

const deleteEntity = async (id: string) => {
  const url = `${apiBaseURL}/${entityApiRoute}/${id}`;
  await axiosInstance.delete(url);
}

const fetchPaginated = async (page: number, filter: string) => {
  const url = `${apiBaseURL}/${entityApiRoute}?page=${page}&filter=${filter}`;
  const response = await axiosInstance.get(url);
  const result: Paginated<Servicio> = response.data?.result;
  return result;
}

const fetchOne = async (id: string): Promise<Servicio | undefined> => {
  const url = `${apiBaseURL}/${entityApiRoute}/${id}`;
  try {
    const response = await axiosInstance.get(url);
    let entity = response.data?.result;
    entity = Utils.removeNullValues(entity);
    return entity;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("No se encontraron datos con el ID proporcionado");
    }
    throw new Error("Ocurrio un error al obtener el dato. " + error.message);
  }
}

const fetchTiposDeServicio = async () => {
  const url = `${apiBaseURL}/${entityApiRoute}/tipos`;
  const response = await axiosInstance.get(url);
  const result: TipoServicio[] = response.data?.result;
  return result;
}

const fetchEstadosDeServicio = async () => {
  const url = `${apiBaseURL}/${entityApiRoute}/estados`;
  const response = await axiosInstance.get(url);
  const result: EstadoServicio[] = response.data?.result;
  return result;
}

export default {
  createEntity,
  updateEntity,
  deleteEntity,
  fetchPaginated,
  fetchOne,
  fetchTiposDeServicio,
  fetchEstadosDeServicio
}
